import { Anchor, Button, Input, Toast } from '@byecode/ui'
import { ErrorMessage, TagIcon, useAtomAction, useAtomData } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useAsyncFn } from 'react-use'

import { fetchOtherPlatformsAtom } from '@/atoms/application/action'
import { otherPlatformListAtom } from '@/atoms/application/state'
import {
    otherPlatformBackgroundColorMap,
    otherPlatformColorMap,
    otherPlatformDescribeMap,
    otherPlatformIconMap,
    otherPlatformNameMap
} from '@/containers/OtherPlatforms/constant'
import * as srv from '@/services'

import { PLACEHOLDER_PASSWORD } from '../constants'
import * as SC from '../styles'
import type { NetEaseFreeAccountFormState } from './type'

interface NetEaseFreeAccountProps {
    onClose?: () => void
}

const initData: NetEaseFreeAccountFormState = {
    id: '',
    senderName: '',
    emailAddress: '',
    password: ''
}
const NetEaseFreeAccount: React.FunctionComponent<NetEaseFreeAccountProps> = ({ onClose }) => {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const { run: fetchOtherPlatforms } = useAtomAction(fetchOtherPlatformsAtom)

    const account = useMemo(() => {
        const otherPlatform = find(item => item.type === 'NET_EASE_126_MAIL', otherPlatforms ?? [])
        return otherPlatform?.configurationComplete
            ? { ...otherPlatform?.config, password: PLACEHOLDER_PASSWORD, id: otherPlatform.id }
            : { ...initData, id: otherPlatform?.id }
    }, [otherPlatforms])

    const {
        control,
        register,
        handleSubmit,
        getValues,
        formState: { errors }
    } = useForm<NetEaseFreeAccountFormState>({
        mode: 'onSubmit',
        defaultValues: account
    })

    const [{ loading }, doUpdateIntegration] = useAsyncFn(srv.updateIntegration)

    const handleUpdate = useCallback(
        async (params: NetEaseFreeAccountFormState) => {
            const { id, senderName, emailAddress, password } = params
            if (!id || loading) {
                return
            }
            const res = await doUpdateIntegration({
                id,
                type: 'NET_EASE_126_MAIL',
                config: { senderName, emailAddress, password: password === PLACEHOLDER_PASSWORD ? undefined : password }
            })

            if (res) {
                fetchOtherPlatforms()
                onClose?.()
                return
            }
            Toast.error('请检查输入邮箱地址或密码是否正确。')
        },
        [doUpdateIntegration, fetchOtherPlatforms, onClose, loading]
    )

    const handleInviteSubmit = useCallback(
        (ev: React.FormEvent) => {
            ev.preventDefault()
            handleSubmit(handleUpdate)()
        },
        [handleSubmit, handleUpdate]
    )

    return (
        <SC.Container onSubmit={handleInviteSubmit} autoComplete="off">
            <SC.Content>
                <TagIcon
                    size={48}
                    iconSize={32}
                    icon={otherPlatformIconMap['NET_EASE_126_MAIL']}
                    radius={6}
                    iconColor={otherPlatformColorMap['NET_EASE_126_MAIL']}
                    background={otherPlatformBackgroundColorMap['NET_EASE_126_MAIL']}
                />
                <SC.Title>{otherPlatformNameMap['NET_EASE_126_MAIL']}</SC.Title>
                <SC.Item>
                    <SC.Description>{otherPlatformDescribeMap['NET_EASE_126_MAIL']}</SC.Description>
                </SC.Item>
                <SC.Tip>
                    <SC.Icon color="var(--color-main)" type="WarningCircle" />
                    <SC.Text>如果您完善授权时遇到问题，请查看帮助文档：</SC.Text>
                    <Anchor
                        target="_blank"
                        style={{ fontWeight: 600, color: 'var(--color-main)' }}
                        to="https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/rd46e4lvxu5uhff9?singleDoc#"
                    >
                        126网易邮箱授权配置
                    </Anchor>
                </SC.Tip>
                <SC.Item>
                    <SC.Text>
                        发件人名称
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="senderName" errors={errors}>
                        <Input {...register('senderName', { required: '不能为空' })} />
                    </ErrorMessage>
                    <SC.Description>收件人看到的名称，请检查核对</SC.Description>
                </SC.Item>

                <SC.Item>
                    <SC.Text>
                        发件人名邮箱地址
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="emailAddress" errors={errors}>
                        <Input {...register('emailAddress', { required: '不能为空' })} />
                    </ErrorMessage>

                    <SC.Description>
                        如address@exmail.qq.com，请先在网易免费邮箱开启
                        <Anchor
                            style={{ color: 'var(--color-main)' }}
                            target="_blank"
                            to="https://help.mail.163.com/faqDetail.do?code=d7a5dc8471cd0c0e8b4b8f4f8e49998b374173cfe9171305fa1ce630d7f67ac25ef2e192b234ae4d"
                        >
                            SMTP
                        </Anchor>
                        服务
                    </SC.Description>
                </SC.Item>
                <SC.Item>
                    <SC.Text>
                        邮箱授权码
                        <SC.Text color="red" style={{ paddingLeft: 4 }}>
                            *
                        </SC.Text>
                    </SC.Text>
                    <ErrorMessage name="password" errors={errors}>
                        <Controller
                            name="password"
                            control={control}
                            rules={{ required: '不能为空' }}
                            render={({ field }) => {
                                return (
                                    <Input
                                        autoComplete="new-password"
                                        type="password"
                                        onFocus={() => {
                                            if (field.value === PLACEHOLDER_PASSWORD) {
                                                field.onChange('')
                                            }
                                        }}
                                        onBlur={() => {
                                            if (!field.value.trim()) {
                                                field.onChange(PLACEHOLDER_PASSWORD)
                                            }
                                        }}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )
                            }}
                        />
                    </ErrorMessage>
                </SC.Item>
            </SC.Content>
            <SC.Footer>
                <SC.LeftFill />
                <SC.RightFill>
                    <Button size="lg" style={{ width: 104 }} onClick={() => onClose?.()}>
                        取消
                    </Button>
                    <Button size="lg" loading={loading} style={{ width: 104 }} type="primary" htmlType="submit">
                        确定
                    </Button>
                </SC.RightFill>
            </SC.Footer>
        </SC.Container>
    )
}

export default NetEaseFreeAccount
